import type { IUserConsumables } from '@experiences/interfaces';

import { get } from '../utility/Requests';

const userUsageUri = '/lease_/api/user-usage';

const directLaCallProps = () => ({
    stripPortalPath: true as const,
    headers: { extendRequestHeaders: 'true' },
});

type UserUsageUserStatus = 'MonthlyLimit' | 'TopUp' | 'PaidUnits' | 0 | 1 | 2;

export interface IUserUsageUserConsumptionDto {
    userId: string;
    email?: string;
    displayName?: string;
    licenses: string[];
    strongestLicense?: string;
    entitlementPoolPercentConsumed: number | null;
    userPoolPercentConsumed: number | null;
    topups: number | null;
    status: UserUsageUserStatus;
}

export interface IUserUsageOrgSummaryDto {
    totalUsersWithConsumption: number;
}

interface IPagedResult<T> {
    totalCount: number;
    results?: T[];
}

interface IUserUsagePagingOptions {
    top?: number;
    skip?: number;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
}

export function countUsersWithConsumption(accountId: string) {
    return get<IUserUsageOrgSummaryDto>(
        `${userUsageUri}/${accountId}/users/count`,
        directLaCallProps(),
    );
}

export function getUsersConsumption(
    accountId: string,
    paging: IUserUsagePagingOptions = {},
    userBundleLicenseCodes?: string[],
) {
    return get<IPagedResult<IUserUsageUserConsumptionDto>>(
        `${userUsageUri}/${accountId}/users`,
        {
            ...directLaCallProps(),
            urlParams: {
                top: paging.top,
                skip: paging.skip,
                sortBy: paging.sortBy,
                sortOrder: paging.sortOrder,
                userBundleLicenseCodes,
            },
            shouldEnumerate: true,
        },
    );
}

export function getSingleUserConsumption(accountId: string, userId: string) {
    return get<IUserUsageUserConsumptionDto>(
        `${userUsageUri}/${accountId}/users/${userId}`,
        directLaCallProps(),
    );
}

// Admin view of a user's consumables: same shape as the self-view endpoint, but without its
// visibility policy — the top-up wallet is returned even when the user no longer holds a
// license/monthly pool. Proxied through apps/server (org id injected from the session), so it
// also works locally where the `/lease_` gateway prefix does not resolve.
export function getUserConsumablesForAdmin(userId: string) {
    return get<IUserConsumables>(`/api/usage/admin/user/${userId}/consumables`);
}
